// client/src/components/ExportData.jsx
import React, { useState } from 'react';
import { Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import './ExportData.css'; // Импортируем общие стили

const ExportData = ({ allData, filters }) => {
    const [loading, setLoading] = useState(false);
    const [exportSuccess, setExportSuccess] = useState(false);
    const [selectedTables, setSelectedTables] = useState({
        schools: true,
        monitoring: true,
        districts: true,
        subjects: true,
        classes: true,
        providers: true,
        daily: true,
        roles: true
    });
    const [fileFormat, setFileFormat] = useState('xlsx');

    const handleTableToggle = (table) => {
        setSelectedTables(prev => ({
            ...prev,
            [table]: !prev[table]
        }));
    };

    const selectAll = () => {
        setSelectedTables({
            schools: true,
            monitoring: true,
            districts: true,
            subjects: true,
            classes: true,
            providers: true,
            daily: true,
            roles: true
        });
    };

    const deselectAll = () => {
        setSelectedTables({
            schools: false,
            monitoring: false,
            districts: false,
            subjects: false,
            classes: false,
            providers: false,
            daily: false,
            roles: false
        });
    };

    // Функция для нормализации даты
    const normalizeDate = (dateValue) => {
        if (!dateValue) return null;
        return String(dateValue).substring(0, 10);
    };

    // Фильтрация данных
    const getFilteredData = () => {
        if (!allData?.monitoringData) return { monitoringData: [] };

        let filtered = [...allData.monitoringData];
        
        if (filters.startDate || filters.endDate) {
            filtered = filtered.filter(record => {
                if (!record.date) return false;
                const recordDate = normalizeDate(record.date);
                let startOk = true;
                let endOk = true;
                if (filters.startDate) startOk = recordDate >= filters.startDate;
                if (filters.endDate) endOk = recordDate <= filters.endDate;
                return startOk && endOk;
            });
        }
        
        if (filters.role !== 'all') {
            filtered = filtered.filter(d => d.role === filters.role);
        }
        
        if (filters.district !== 'all') {
            filtered = filtered.filter(d => d.district === filters.district);
        }
        
        return { monitoringData: filtered };
    };

    // Подготовка данных для экспорта
    const prepareExportData = () => {
        const { monitoringData } = getFilteredData();
        const exportData = {};

        // 1. Школы
        if (selectedTables.schools && allData?.schools) {
            exportData.schools = allData.schools.map(school => ({
                'ID': school.id,
                'Название школы': school.school_name,
                'Район': school.district,
                'Всего записей': school.total_records || 0,
                'Всего просмотров': school.total_views || 0,
                'Уникальные пользователи': school.unique_users || 0,
                'Ученики': school.unique_students || 0,
                'Учителя': school.unique_teachers || 0,
                'Предметы': school.subjects_count || 0,
                'Классы': school.classes_count || 0,
                'Поставщики': school.providers_count || 0,
                'Дней активности': school.active_days || 0
            }));
        }

        // 2. Данные мониторинга
        if (selectedTables.monitoring && monitoringData.length > 0) {
            exportData.monitoring = monitoringData.map(record => ({
                'ID школы': record.school_id,
                'Школа': allData?.schools?.find(s => s.id === record.school_id)?.school_name || '',
                'Район': record.district,
                'Дата': record.date,
                'Роль': record.role === 'student' ? 'Ученик' : record.role === 'teacher' ? 'Учитель' : record.role,
                'Пользователь': record.user_alias,
                'Предмет': record.subject,
                'Класс': record.class_level,
                'Поставщик': record.provider,
                'Просмотры': record.views_count
            }));
        }

        // 3. Статистика по районам
        if (selectedTables.districts && allData?.schools) {
            const districtMap = new Map();
            
            allData.schools.forEach(school => {
                if (!school.district) return;
                if (!districtMap.has(school.district)) {
                    districtMap.set(school.district, {
                        district: school.district,
                        total_schools: 0,
                        schools_with_data: new Set(),
                        total_views: 0,
                        unique_users: new Set()
                    });
                }
                const stats = districtMap.get(school.district);
                stats.total_schools++;
            });
            
            monitoringData.forEach(record => {
                if (!record.district) return;
                if (districtMap.has(record.district)) {
                    const stats = districtMap.get(record.district);
                    if (record.school_id) stats.schools_with_data.add(record.school_id);
                    stats.total_views += record.views_count || 0;
                    stats.unique_users.add(record.user_alias);
                }
            });
            
            exportData.districts = Array.from(districtMap.values()).map(d => ({
                'Район': d.district,
                'Всего ОУ': d.total_schools,
                'ОУ с данными': d.schools_with_data.size,
                'Охват (%)': d.total_schools > 0 ? ((d.schools_with_data.size / d.total_schools) * 100).toFixed(1) : 0,
                'Просмотры': d.total_views,
                'Пользователи': d.unique_users.size
            }));
        }

        // 4. Статистика по предметам
        if (selectedTables.subjects && monitoringData.length > 0) {
            const subjectMap = new Map();
            
            monitoringData.forEach(record => {
                if (!record.subject) return;
                if (!subjectMap.has(record.subject)) {
                    subjectMap.set(record.subject, {
                        subject: record.subject,
                        users: new Set(),
                        students: new Set(),
                        teachers: new Set(),
                        total_views: 0
                    });
                }
                const stats = subjectMap.get(record.subject);
                stats.users.add(record.user_alias);
                if (record.role === 'student') stats.students.add(record.user_alias);
                if (record.role === 'teacher') stats.teachers.add(record.user_alias);
                stats.total_views += record.views_count || 0;
            });
            
            exportData.subjects = Array.from(subjectMap.values())
                .map(s => ({
                    'Предмет': s.subject,
                    'Пользователи': s.users.size,
                    'Ученики': s.students.size,
                    'Учителя': s.teachers.size,
                    'Просмотры': s.total_views
                }))
                .sort((a, b) => b.Пользователи - a.Пользователи)
                .slice(0, 50);
        }

        // 5. Статистика по классам
        if (selectedTables.classes && monitoringData.length > 0) {
            const classMap = new Map();
            
            monitoringData.forEach(record => {
                if (!record.class_level) return;
                const classLevel = parseInt(record.class_level);
                if (isNaN(classLevel) || classLevel < 1 || classLevel > 11) return;
                
                if (!classMap.has(classLevel)) {
                    classMap.set(classLevel, {
                        class: classLevel,
                        users: new Set(),
                        students: new Set(),
                        teachers: new Set(),
                        total_views: 0
                    });
                }
                const stats = classMap.get(classLevel);
                stats.users.add(record.user_alias);
                if (record.role === 'student') stats.students.add(record.user_alias);
                if (record.role === 'teacher') stats.teachers.add(record.user_alias);
                stats.total_views += record.views_count || 0;
            });
            
            exportData.classes = Array.from(classMap.values())
                .sort((a, b) => a.class - b.class)
                .map(c => ({
                    'Класс': c.class,
                    'Пользователи': c.users.size,
                    'Ученики': c.students.size,
                    'Учителя': c.teachers.size,
                    'Просмотры': c.total_views
                }));
        }

        // 6. Статистика по поставщикам
        if (selectedTables.providers && monitoringData.length > 0) {
            const providerMap = new Map();
            
            monitoringData.forEach(record => {
                if (!record.provider) return;
                if (!providerMap.has(record.provider)) {
                    providerMap.set(record.provider, {
                        provider: record.provider,
                        users: new Set(),
                        students: new Set(),
                        teachers: new Set(),
                        total_views: 0
                    });
                }
                const stats = providerMap.get(record.provider);
                stats.users.add(record.user_alias);
                if (record.role === 'student') stats.students.add(record.user_alias);
                if (record.role === 'teacher') stats.teachers.add(record.user_alias);
                stats.total_views += record.views_count || 0;
            });
            
            exportData.providers = Array.from(providerMap.values())
                .map(p => ({
                    'Поставщик': p.provider,
                    'Пользователи': p.users.size,
                    'Ученики': p.students.size,
                    'Учителя': p.teachers.size,
                    'Просмотры': p.total_views
                }))
                .sort((a, b) => b.Пользователи - a.Пользователи)
                .slice(0, 30);
        }

        // 7. Динамика по дням
        if (selectedTables.daily && monitoringData.length > 0) {
            const dayMap = new Map();
            
            monitoringData.forEach(record => {
                if (!record.date) return;
                const dateKey = normalizeDate(record.date);
                if (!dateKey) return;
                
                if (!dayMap.has(dateKey)) {
                    dayMap.set(dateKey, {
                        date: dateKey,
                        users: new Set(),
                        students: new Set(),
                        teachers: new Set(),
                        total_views: 0
                    });
                }
                const stats = dayMap.get(dateKey);
                stats.users.add(record.user_alias);
                if (record.role === 'student') stats.students.add(record.user_alias);
                if (record.role === 'teacher') stats.teachers.add(record.user_alias);
                stats.total_views += record.views_count || 0;
            });
            
            exportData.daily = Array.from(dayMap.values())
                .sort((a, b) => a.date.localeCompare(b.date))
                .map(d => ({
                    'Дата': d.date,
                    'Пользователи': d.users.size,
                    'Ученики': d.students.size,
                    'Учителя': d.teachers.size,
                    'Просмотры': d.total_views
                }));
        }

        // 8. Статистика по ролям
        if (selectedTables.roles && monitoringData.length > 0) {
            const students = new Set();
            const teachers = new Set();
            
            monitoringData.forEach(record => {
                if (record.role === 'student') students.add(record.user_alias);
                if (record.role === 'teacher') teachers.add(record.user_alias);
            });
            
            exportData.roles = [
                { 'Роль': 'Ученики', 'Количество': students.size },
                { 'Роль': 'Учителя', 'Количество': teachers.size },
                { 'Роль': 'Всего', 'Количество': students.size + teachers.size }
            ];
        }

        return exportData;
    };

    const handleExport = async () => {
        setLoading(true);
        setExportSuccess(false);
        
        try {
            const exportData = prepareExportData();
            const wb = XLSX.utils.book_new();
            
            // Добавляем листы в книгу
            if (exportData.schools) {
                const ws = XLSX.utils.json_to_sheet(exportData.schools);
                XLSX.utils.book_append_sheet(wb, ws, 'Школы');
            }
            
            if (exportData.monitoring) {
                const ws = XLSX.utils.json_to_sheet(exportData.monitoring);
                XLSX.utils.book_append_sheet(wb, ws, 'Данные мониторинга');
            }
            
            if (exportData.districts) {
                const ws = XLSX.utils.json_to_sheet(exportData.districts);
                XLSX.utils.book_append_sheet(wb, ws, 'Районы');
            }
            
            if (exportData.subjects) {
                const ws = XLSX.utils.json_to_sheet(exportData.subjects);
                XLSX.utils.book_append_sheet(wb, ws, 'Предметы');
            }
            
            if (exportData.classes) {
                const ws = XLSX.utils.json_to_sheet(exportData.classes);
                XLSX.utils.book_append_sheet(wb, ws, 'Классы');
            }
            
            if (exportData.providers) {
                const ws = XLSX.utils.json_to_sheet(exportData.providers);
                XLSX.utils.book_append_sheet(wb, ws, 'Поставщики');
            }
            
            if (exportData.daily) {
                const ws = XLSX.utils.json_to_sheet(exportData.daily);
                XLSX.utils.book_append_sheet(wb, ws, 'Динамика');
            }
            
            if (exportData.roles) {
                const ws = XLSX.utils.json_to_sheet(exportData.roles);
                XLSX.utils.book_append_sheet(wb, ws, 'Роли');
            }
            
            // Добавляем лист с информацией о фильтрах
            const filterInfo = [
                { 'Параметр': 'Район', 'Значение': filters.district === 'all' ? 'Все' : filters.district },
                { 'Параметр': 'Роль', 'Значение': filters.role === 'all' ? 'Все' : (filters.role === 'student' ? 'Ученики' : 'Учителя') },
                { 'Параметр': 'Дата с', 'Значение': filters.startDate || 'Не указана' },
                { 'Параметр': 'Дата по', 'Значение': filters.endDate || 'Не указана' },
                { 'Параметр': 'Дата экспорта', 'Значение': new Date().toLocaleString('ru-RU') }
            ];
            const wsInfo = XLSX.utils.json_to_sheet(filterInfo);
            XLSX.utils.book_append_sheet(wb, wsInfo, 'Информация');
            
            // Сохраняем файл
            const fileName = `ub_cok_export_${new Date().toISOString().split('T')[0]}.${fileFormat}`;
            XLSX.writeFile(wb, fileName);
            
            setExportSuccess(true);
            setTimeout(() => setExportSuccess(false), 5000);
        } catch (error) {
            console.error('Ошибка экспорта:', error);
            alert('Произошла ошибка при экспорте данных');
        } finally {
            setLoading(false);
        }
    };

    const totalSelected = Object.values(selectedTables).filter(v => v).length;

    return (
        <div className="export-container">
            <div className="export-header">
                <h5><i className="bi bi-file-earmark-spreadsheet"></i> Экспорт всех данных</h5>
                <p>Выберите данные для экспорта и нажмите кнопку для выгрузки в Excel</p>
            </div>

            <div className="export-filters-info">
                <Alert variant="info" className="mb-3">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Активные фильтры:</strong>
                    {filters.district !== 'all' && <span className="badge bg-primary ms-2">Район: {filters.district}</span>}
                    {filters.role !== 'all' && <span className="badge bg-success ms-2">Роль: {filters.role === 'student' ? 'Ученики' : 'Учителя'}</span>}
                    {filters.startDate && <span className="badge bg-info ms-2">С: {filters.startDate}</span>}
                    {filters.endDate && <span className="badge bg-info ms-2">По: {filters.endDate}</span>}
                    {filters.district === 'all' && filters.role === 'all' && !filters.startDate && !filters.endDate && (
                        <span className="ms-2">Все данные (без фильтров)</span>
                    )}
                </Alert>
            </div>

            <Row>
                <Col md={8}>
                    <div className="export-tables-list">
                        <div className="export-tables-header">
                            <span>Выберите данные для экспорта</span>
                            <div className="export-select-all">
                                <button className="export-select-btn" onClick={selectAll}>Выбрать все</button>
                                <button className="export-select-btn" onClick={deselectAll}>Снять все</button>
                            </div>
                        </div>
                        
                        <div className="export-tables-grid">
                            <div className="export-table-item">
                                <label className="export-checkbox">
                                    <input 
                                        type="checkbox" 
                                        checked={selectedTables.schools}
                                        onChange={() => handleTableToggle('schools')}
                                    />
                                    <span>Школы</span>
                                </label>
                                <span className="export-table-desc">Список школ с общей статистикой</span>
                            </div>
                            
                            <div className="export-table-item">
                                <label className="export-checkbox">
                                    <input 
                                        type="checkbox" 
                                        checked={selectedTables.monitoring}
                                        onChange={() => handleTableToggle('monitoring')}
                                    />
                                    <span>Данные мониторинга</span>
                                </label>
                                <span className="export-table-desc">Детальные записи использования</span>
                            </div>
                            
                            <div className="export-table-item">
                                <label className="export-checkbox">
                                    <input 
                                        type="checkbox" 
                                        checked={selectedTables.districts}
                                        onChange={() => handleTableToggle('districts')}
                                    />
                                    <span>Районы</span>
                                </label>
                                <span className="export-table-desc">Статистика по районам</span>
                            </div>
                            
                            <div className="export-table-item">
                                <label className="export-checkbox">
                                    <input 
                                        type="checkbox" 
                                        checked={selectedTables.subjects}
                                        onChange={() => handleTableToggle('subjects')}
                                    />
                                    <span>Предметы</span>
                                </label>
                                <span className="export-table-desc">Статистика по предметам</span>
                            </div>
                            
                            <div className="export-table-item">
                                <label className="export-checkbox">
                                    <input 
                                        type="checkbox" 
                                        checked={selectedTables.classes}
                                        onChange={() => handleTableToggle('classes')}
                                    />
                                    <span>Классы</span>
                                </label>
                                <span className="export-table-desc">Статистика по классам</span>
                            </div>
                            
                            <div className="export-table-item">
                                <label className="export-checkbox">
                                    <input 
                                        type="checkbox" 
                                        checked={selectedTables.providers}
                                        onChange={() => handleTableToggle('providers')}
                                    />
                                    <span>Поставщики</span>
                                </label>
                                <span className="export-table-desc">Статистика по поставщикам</span>
                            </div>
                            
                            <div className="export-table-item">
                                <label className="export-checkbox">
                                    <input 
                                        type="checkbox" 
                                        checked={selectedTables.daily}
                                        onChange={() => handleTableToggle('daily')}
                                    />
                                    <span>Динамика</span>
                                </label>
                                <span className="export-table-desc">Активность по дням</span>
                            </div>
                            
                            <div className="export-table-item">
                                <label className="export-checkbox">
                                    <input 
                                        type="checkbox" 
                                        checked={selectedTables.roles}
                                        onChange={() => handleTableToggle('roles')}
                                    />
                                    <span>Роли</span>
                                </label>
                                <span className="export-table-desc">Соотношение учеников и учителей</span>
                            </div>
                        </div>
                    </div>
                </Col>
                
                <Col md={4}>
                    <div className="export-options">
                        <div className="export-options-header">
                            <i className="bi bi-gear"></i> Настройки экспорта
                        </div>
                        
                        <div className="export-format">
                            <label>Формат файла:</label>
                            <div className="export-format-buttons">
                                <button 
                                    className={`export-format-btn ${fileFormat === 'xlsx' ? 'active' : ''}`}
                                    onClick={() => setFileFormat('xlsx')}
                                >
                                    XLSX
                                </button>
                                <button 
                                    className={`export-format-btn ${fileFormat === 'csv' ? 'active' : ''}`}
                                    onClick={() => setFileFormat('csv')}
                                    disabled
                                >
                                    CSV (скоро)
                                </button>
                            </div>
                        </div>
                        
                        <div className="export-summary">
                            <div className="export-summary-item">
                                <span>Выбрано таблиц:</span>
                                <strong>{totalSelected} из 8</strong>
                            </div>
                        </div>
                        
                        <button 
                            className="export-btn-main"
                            onClick={handleExport}
                            disabled={loading || totalSelected === 0}
                        >
                            {loading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Экспорт...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-file-excel me-2"></i>
                                    Экспортировать в {fileFormat.toUpperCase()}
                                </>
                            )}
                        </button>
                        
                        {exportSuccess && (
                            <div className="export-success">
                                <i className="bi bi-check-circle-fill"></i>
                                Экспорт завершен! Файл сохранен.
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default ExportData;